import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, orderBy, doc, setDoc, Timestamp, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Package, Clock, Truck, CheckCircle, XCircle, Search, ChevronRight, Star, RotateCcw, MessageSquare, FileText, ShoppingCart, AlertTriangle } from 'lucide-react';
import ProductReview from './ProductReview';
import OrderTracking from './OrderTracking';
import CancelRequestModal from './CancelRequestModal';
import TaxInvoiceRequest from './TaxInvoiceRequest';
import RefundRequest from './RefundRequest';

const MyOrders = ({ userInfo }) => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewTarget, setReviewTarget] = useState(null);
    const [showTrackingModal, setShowTrackingModal] = useState(false);
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelOrder, setCancelOrder] = useState(null);
    const [showTaxModal, setShowTaxModal] = useState(false);
    const [taxOrder, setTaxOrder] = useState(null);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [refundOrder, setRefundOrder] = useState(null);

    useEffect(() => {
        if (userInfo) {
            loadOrders();
        }
    }, [userInfo]);

    const loadOrders = async () => {
        try {
            const ordersQuery = query(
                collection(db, 'marketplaceOrders'),
                where('buyerId', '==', userInfo.uid),
                orderBy('createdAt', 'desc')
            );

            const snapshot = await getDocs(ordersQuery);
            const ordersData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setOrders(ordersData);
        } catch (error) {
            console.error('주문 내역 로드 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusInfo = (status) => {
        const statusMap = {
            pending_payment: {
                label: '입금대기',
                color: '#f59e0b',
                icon: Clock,
                bgColor: '#fef3c7'
            },
            placed: {
                label: '주문접수',
                color: '#f59e0b',
                icon: Clock,
                bgColor: '#fef3c7'
            },
            pending: {
                label: '주문접수',
                color: '#f59e0b',
                icon: Clock,
                bgColor: '#fef3c7'
            },
            paid: {
                label: '주문접수',
                color: '#f59e0b',
                icon: Clock,
                bgColor: '#fef3c7'
            },
            confirmed: {
                label: '주문확인',
                color: '#3b82f6',
                icon: CheckCircle,
                bgColor: '#dbeafe'
            },
            preparing: {
                label: '배송 준비 중',
                color: '#6366f1',
                icon: Package,
                bgColor: '#e0e7ff'
            },
            shipped: {
                label: '배송중',
                color: '#06b6d4',
                icon: Truck,
                bgColor: '#cffafe'
            },
            shipping: {
                label: '배송중',
                color: '#06b6d4',
                icon: Truck,
                bgColor: '#cffafe'
            },
            delivered: {
                label: '수령완료',
                color: '#10b981',
                icon: CheckCircle,
                bgColor: '#d1fae5'
            },
            cancel_requested: {
                label: '취소요청',
                color: '#f97316',
                icon: AlertTriangle,
                bgColor: '#fff7ed'
            },
            cancelled: {
                label: '취소완료',
                color: '#ef4444',
                icon: XCircle,
                bgColor: '#fee2e2'
            }
        };
        return statusMap[status] || statusMap.placed;
    };

    // 수령확인 (shipped → delivered)
    const handleConfirmDelivery = async (orderId) => {
        if (!window.confirm('수령을 확인하시겠습니까?')) return;
        try {
            await updateDoc(doc(db, 'marketplaceOrders', orderId), {
                status: 'delivered',
                deliveredAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            loadOrders();
            alert('수령이 확인되었습니다.');
        } catch (error) {
            console.error('수령확인 실패:', error);
            alert('수령확인에 실패했습니다.');
        }
    };

    // 재주문 (장바구니에 동일 상품 담기)
    const handleReorder = async (order) => {
        if (!userInfo?.uid) return;
        const userId = userInfo.uid;
        try {
            const cartRef = doc(db, 'userCarts', userId);
            const items = order.items.map(item => ({
                productId: item.productId || item.id,
                productName: item.productName,
                price: item.price,
                quantity: item.quantity,
                imageUrl: item.image || '',
                sellerId: item.sellerId || order.sellerId || '',
                sellerName: item.sellerName || order.sellerName || '',
                addedAt: Timestamp.now()
            }));
            await setDoc(cartRef, { items, updatedAt: Timestamp.now() }, { merge: true });
            alert('장바구니에 담았습니다.');
            navigate('/cart');
        } catch (error) {
            console.error('재주문 실패:', error);
            alert('재주문에 실패했습니다.');
        }
    };

    // 세금계산서 요청 가능 여부 (delivered + 10일 이내)
    const canRequestTaxInvoice = (order) => {
        if (order.status !== 'delivered') return false;
        const deliveredAt = order.deliveredAt?.toDate ? order.deliveredAt.toDate() : order.deliveredAt ? new Date(order.deliveredAt) : null;
        if (!deliveredAt) return true; // deliveredAt 없으면 일단 허용
        const diff = (new Date() - deliveredAt) / (1000 * 60 * 60 * 24);
        return diff <= 10;
    };

    const filteredOrders = orders.filter(order => {
        let matchesStatus = false;
        if (filterStatus === 'all') matchesStatus = true;
        else if (filterStatus === 'active') matchesStatus = ['pending_payment', 'placed', 'pending', 'paid', 'confirmed', 'preparing', 'shipped', 'shipping'].includes(order.status);
        else if (filterStatus === 'delivered') matchesStatus = order.status === 'delivered';
        else if (filterStatus === 'cancel') matchesStatus = ['cancel_requested', 'cancelled'].includes(order.status);
        else matchesStatus = order.status === filterStatus;

        const matchesSearch = searchTerm === '' ||
            order.items?.some(item =>
                item.productName?.toLowerCase().includes(searchTerm.toLowerCase())
            ) ||
            order.id.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Package size={48} color="#6366f1" />
                <p style={styles.loadingText}>주문 내역을 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 필터 & 검색 */}
            <div style={styles.filters}>
                <div style={styles.statusFilters}>
                    {[
                        { value: 'all', label: '전체' },
                        { value: 'active', label: '진행중' },
                        { value: 'delivered', label: '완료' },
                        { value: 'cancel', label: '취소' }
                    ].map(status => (
                        <button
                            key={status.value}
                            style={{
                                ...styles.filterButton,
                                ...(filterStatus === status.value ? styles.filterButtonActive : {})
                            }}
                            onClick={() => setFilterStatus(status.value)}
                        >
                            {status.label}
                        </button>
                    ))}
                </div>

                <div style={styles.searchBox}>
                    <Search size={20} color="#94a3b8" />
                    <input
                        type="text"
                        placeholder="주문번호 또는 상품명 검색"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                </div>
            </div>

            {/* 주문 목록 */}
            {filteredOrders.length === 0 ? (
                <div style={styles.emptyState}>
                    <Package size={64} color="#cbd5e1" />
                    <p style={styles.emptyText}>
                        {searchTerm || filterStatus !== 'all' 
                            ? '검색 결과가 없습니다' 
                            : '주문 내역이 없습니다'}
                    </p>
                    {!searchTerm && filterStatus === 'all' && (
                        <button 
                            style={styles.shopButton}
                            onClick={() => navigate('/marketplace')}
                        >
                            쇼핑하러 가기
                        </button>
                    )}
                </div>
            ) : (
                <div style={styles.ordersList}>
                    {filteredOrders.map(order => {
                        const statusInfo = getStatusInfo(order.status);
                        const StatusIcon = statusInfo.icon;

                        return (
                            <div key={order.id} style={styles.orderCard}>
                                {/* 주문 헤더 */}
                                <div style={styles.orderHeader}>
                                    <div style={styles.orderHeaderLeft}>
                                        <span style={styles.orderDate}>
                                            {formatDate(order.createdAt)}
                                        </span>
                                        <span style={styles.orderDivider}>•</span>
                                        <span style={styles.orderId}>
                                            주문번호: {order.id.slice(0, 8)}
                                        </span>
                                    </div>
                                    <div 
                                        style={{
                                            ...styles.statusBadge,
                                            backgroundColor: statusInfo.bgColor,
                                            color: statusInfo.color
                                        }}
                                    >
                                        <StatusIcon size={16} />
                                        <span>{statusInfo.label}</span>
                                    </div>
                                </div>

                                {/* 주문 상품 */}
                                <div style={styles.orderItems}>
                                    {order.items.map((item, index) => (
                                        <div key={index} style={styles.orderItem}>
                                            <img 
                                                src={item.image || '/placeholder-product.png'}
                                                alt={item.productName}
                                                style={styles.itemImage}
                                            />
                                            <div style={styles.itemInfo}>
                                                <p style={styles.itemName}>{item.productName}</p>
                                                <p style={styles.itemDetails}>
                                                    {(item.price || 0).toLocaleString()}원 × {item.quantity || 0}개
                                                </p>
                                            </div>
                                            <div style={styles.itemTotal}>
                                                {((item.price || 0) * (item.quantity || 0)).toLocaleString()}원
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* 배송 추적 정보 */}
                                {order.shipping?.trackingNumber && (
                                    <div style={styles.trackingBar}>
                                        <Truck size={16} color="#6366f1" />
                                        <span style={styles.trackingText}>
                                            {order.shipping.carrier} {order.shipping.trackingNumber}
                                        </span>
                                        <button
                                            style={styles.trackingLinkButton}
                                            onClick={() => {
                                                setTrackingOrder(order);
                                                setShowTrackingModal(true);
                                            }}
                                        >
                                            배송 조회
                                        </button>
                                    </div>
                                )}

                                {/* 입금대기 안내 */}
                                {order.status === 'pending_payment' && order.payment?.sellerBankInfo && (
                                    <div style={{
                                        padding: 16, marginBottom: 16,
                                        backgroundColor: '#fef3c7', borderRadius: 12,
                                        border: '1px solid #fde68a'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                            <Clock size={18} color="#f59e0b" />
                                            <span style={{ fontSize: 15, fontWeight: 700, color: '#92400e' }}>입금 안내</span>
                                        </div>
                                        {order.payment.sellerBankInfo.bankName ? (
                                            <div style={{
                                                padding: 12, backgroundColor: '#fff',
                                                borderRadius: 8, border: '1px solid #fde68a'
                                            }}>
                                                <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                                                    {order.payment.sellerBankInfo.bankName} {order.payment.sellerBankInfo.bankAccount}
                                                </p>
                                                <p style={{ margin: 0, fontSize: 14, color: '#475569' }}>
                                                    예금주: {order.payment.sellerBankInfo.bankHolder}
                                                </p>
                                            </div>
                                        ) : (
                                            <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
                                                판매자 계좌 정보가 등록되지 않았습니다. 판매자에게 문의해주세요.
                                            </p>
                                        )}
                                        {order.payment.depositorName && (
                                            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#92400e' }}>
                                                입금자명: {order.payment.depositorName}
                                            </p>
                                        )}
                                        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#b45309' }}>
                                            위 계좌로 입금하시면 판매자 확인 후 배송이 시작됩니다.
                                        </p>
                                    </div>
                                )}

                                {/* 주문 푸터 */}
                                <div style={styles.orderFooter}>
                                    <div style={styles.orderTotal}>
                                        <span style={styles.totalLabel}>총 결제 금액</span>
                                        <span style={styles.totalAmount}>
                                            {(order.payment?.total || order.total || order.totalAmount || 0).toLocaleString()}원
                                        </span>
                                    </div>
                                    <div style={styles.footerActions}>
                                        {/* 수령확인 버튼 (shipped → delivered) */}
                                        {(order.status === 'shipped' || order.status === 'shipping') && (
                                            <button
                                                style={{...styles.reviewButton, color: '#10b981', backgroundColor: '#dcfce7'}}
                                                onClick={() => handleConfirmDelivery(order.id)}
                                            >
                                                <CheckCircle size={16} />
                                                수령확인
                                            </button>
                                        )}
                                        {/* 취소 요청 (placed/confirmed 상태) */}
                                        {['pending_payment', 'placed', 'pending', 'paid', 'confirmed'].includes(order.status) && (
                                            <button
                                                style={styles.refundButton}
                                                onClick={() => { setCancelOrder(order); setShowCancelModal(true); }}
                                            >
                                                <XCircle size={16} />
                                                취소 요청
                                            </button>
                                        )}
                                        {/* 배송 완료 후 리뷰 작성 */}
                                        {order.status === 'delivered' && (
                                            <button
                                                style={styles.reviewButton}
                                                onClick={() => {
                                                    const item = order.items[0];
                                                    setReviewTarget({
                                                        productId: item.productId || item.id,
                                                        orderId: order.id,
                                                        productName: item.productName,
                                                        productImageUrl: item.image
                                                    });
                                                    setShowReviewModal(true);
                                                }}
                                            >
                                                <Star size={16} />
                                                리뷰 작성
                                            </button>
                                        )}
                                        {/* 세금계산서 요청 (delivered + 10일 이내) */}
                                        {canRequestTaxInvoice(order) && (
                                            <button
                                                style={{...styles.reviewButton, color: '#6366f1', backgroundColor: '#eef2ff'}}
                                                onClick={() => { setTaxOrder(order); setShowTaxModal(true); }}
                                            >
                                                <FileText size={16} />
                                                세금계산서
                                            </button>
                                        )}
                                        {/* 재주문 (완료/취소) */}
                                        {['delivered', 'cancelled'].includes(order.status) && (
                                            <button
                                                style={{...styles.reviewButton, color: '#6366f1', backgroundColor: '#eef2ff'}}
                                                onClick={() => handleReorder(order)}
                                            >
                                                <ShoppingCart size={16} />
                                                재주문
                                            </button>
                                        )}
                                        {/* 반품/환불 */}
                                        {(order.status === 'delivered' || order.status === 'shipped' || order.status === 'shipping') && !order.refundStatus && (
                                            <button
                                                style={styles.refundButton}
                                                onClick={() => { setRefundOrder(order); setShowRefundModal(true); }}
                                            >
                                                <RotateCcw size={16} />
                                                반품/환불
                                            </button>
                                        )}
                                        {/* 주문 문의 */}
                                        <button
                                            style={styles.chatButton}
                                            onClick={() => navigate('/chat', {
                                                state: {
                                                    type: 'order_inquiry',
                                                    targetUserId: order.items[0]?.sellerId,
                                                    orderId: order.id,
                                                    productName: order.items[0]?.productName
                                                }
                                            })}
                                        >
                                            <MessageSquare size={16} />
                                        </button>
                                        <button
                                            style={styles.detailButton}
                                            onClick={() => {
                                                setTrackingOrder(order);
                                                setShowTrackingModal(true);
                                            }}
                                        >
                                            <span>상세보기</span>
                                            <ChevronRight size={20} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {/* 리뷰 작성 모달 */}
            {showReviewModal && reviewTarget && (
                <ProductReview
                    userInfo={userInfo}
                    productId={reviewTarget.productId}
                    orderId={reviewTarget.orderId}
                    productName={reviewTarget.productName}
                    productImageUrl={reviewTarget.productImageUrl}
                    onClose={() => {
                        setShowReviewModal(false);
                        setReviewTarget(null);
                    }}
                    onSuccess={() => {
                        setShowReviewModal(false);
                        setReviewTarget(null);
                        loadOrders();
                    }}
                />
            )}

            {/* 취소 요청 모달 */}
            {showCancelModal && cancelOrder && (
                <CancelRequestModal
                    order={cancelOrder}
                    userInfo={userInfo}
                    onClose={() => { setShowCancelModal(false); setCancelOrder(null); }}
                    onSuccess={() => { loadOrders(); }}
                />
            )}

            {/* 세금계산서 요청 모달 */}
            {showTaxModal && taxOrder && (
                <TaxInvoiceRequest
                    order={taxOrder}
                    userInfo={userInfo}
                    onClose={() => { setShowTaxModal(false); setTaxOrder(null); }}
                    onSuccess={() => { alert('세금계산서 요청이 완료되었습니다.'); }}
                />
            )}

            {/* 반품/환불 모달 */}
            {showRefundModal && refundOrder && (
                <RefundRequest
                    order={refundOrder}
                    userInfo={userInfo}
                    onClose={() => { setShowRefundModal(false); setRefundOrder(null); }}
                    onSubmitted={() => { setShowRefundModal(false); setRefundOrder(null); loadOrders(); alert('반품/환불 요청이 접수되었습니다.'); }}
                />
            )}

            {/* 배송 추적 모달 */}
            {showTrackingModal && trackingOrder && (
                <div style={styles.modalOverlay} onClick={() => setShowTrackingModal(false)}>
                    <div style={styles.trackingModal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.trackingModalHeader}>
                            <h3 style={styles.trackingModalTitle}>배송 추적</h3>
                            <button
                                style={styles.trackingModalClose}
                                onClick={() => setShowTrackingModal(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <OrderTracking order={trackingOrder} />
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px 24px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    filters: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '24px',
        padding: '24px',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    statusFilters: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    filterButton: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        border: '2px solid transparent',
        borderRadius: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    filterButtonActive: {
        color: '#6366f1',
        backgroundColor: '#f0f1ff',
        borderColor: '#6366f1',
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '2px solid #e2e8f0',
    },
    searchInput: {
        flex: 1,
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: '15px',
        outline: 'none',
    },
    ordersList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    orderCard: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s',
    },
    orderHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '2px solid #f1f5f9',
    },
    orderHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    orderDate: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1e293b',
    },
    orderDivider: {
        color: '#cbd5e1',
    },
    orderId: {
        fontSize: '14px',
        color: '#64748b',
    },
    statusBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: '600',
    },
    orderItems: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '20px',
    },
    orderItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '12px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
    },
    itemImage: {
        width: '70px',
        height: '70px',
        objectFit: 'cover',
        borderRadius: '8px',
    },
    itemInfo: {
        flex: 1,
    },
    itemName: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1e293b',
        margin: '0 0 6px 0',
    },
    itemDetails: {
        fontSize: '14px',
        color: '#64748b',
        margin: 0,
    },
    itemTotal: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
    },
    orderFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '16px',
        borderTop: '2px solid #f1f5f9',
    },
    orderTotal: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    totalLabel: {
        fontSize: '14px',
        color: '#64748b',
    },
    totalAmount: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#6366f1',
    },
    detailButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#f0f1ff',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '100px 24px',
        backgroundColor: 'white',
        borderRadius: '16px',
    },
    emptyText: {
        fontSize: '18px',
        color: '#64748b',
        margin: '24px 0',
    },
    shopButton: {
        padding: '14px 32px',
        fontSize: '16px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        gap: '16px',
    },
    loadingText: {
        fontSize: '16px',
        color: '#64748b',
    },
    trackingBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        backgroundColor: '#eef2ff',
        borderRadius: '10px',
        marginBottom: '16px',
    },
    trackingText: {
        flex: 1,
        fontSize: '13px',
        fontWeight: '600',
        color: '#6366f1',
    },
    trackingLinkButton: {
        padding: '6px 12px',
        fontSize: '12px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: 'white',
        border: '1px solid #6366f1',
        borderRadius: '6px',
        cursor: 'pointer',
    },
    footerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    reviewButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 16px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#f59e0b',
        backgroundColor: '#fef3c7',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
    },
    refundButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 16px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#ef4444',
        backgroundColor: '#fee2e2',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
    },
    chatButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        cursor: 'pointer',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    },
    trackingModal: {
        backgroundColor: 'white',
        borderRadius: '16px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'auto',
    },
    trackingModalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid #e2e8f0',
    },
    trackingModalTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    trackingModalClose: {
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        fontSize: '18px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
};

export default MyOrders;